import React, { forwardRef, useMemo } from 'react';

import { Box, Flex, Menu, MenuButton, MenuGroup, MenuItem, MenuList, Tag } from '@chakra-ui/react';
import styled from '@emotion/styled';

import { Calendar, ChevronDown } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium, TextHeader2 } from 'uiToolkitV1/Typography';

const HeaderText = ({ children }) => (
  <TextHeader2 bold color="#333333">
    {children}
  </TextHeader2>
);

const CommonTimelineSelect = ({
  onChange: handleSelect,
  menuItems,
  otherMenuItems = [],
  selectedValue,
  renderAsRefableComp = forwardRef((props, ref) => <Button ref={ref} variant="tertiary" p={0} {...props} />),
  CurrentTimelineWrapper,
  selectedTimeBoundObject,
  menuButtonSx = {
    p: {
      margin: '0',
    },
    svg: {
      transition: 'transform 150ms ease-in-out',
    },
    '&[aria-expanded="true"]': {
      svg: {
        transform: 'rotate(180deg)',
      },
    },
  },
  showCalendarIcon,
}) => (
  <Menu autoSelect={false} placement="bottom-end">
    <MenuButton sx={menuButtonSx} as={renderAsRefableComp} EndIcon={ChevronDown}>
      <CurrentTimelineWrapper>
        <Flex alignItems="center">
          {showCalendarIcon && <Calendar transform="rotate(0deg) !important" boxSize="18px" color="text.g2" mr="8px" />}

          {selectedTimeBoundObject?.label}
        </Flex>
      </CurrentTimelineWrapper>
    </MenuButton>
    <StyledMenuList maxHeight="200px" overflowY="scroll" p="0">
      {menuItems.map(({ label, value, tag = '' }) => (
        <ItemContainer key={value}>
          <StyledMenuItem value={value} onClick={() => handleSelect(value)} $isSelected={value === selectedValue}>
            <Flex justifyContent="space-between" alignItems="center" width="100%">
              <TextBodyMedium>{label}</TextBodyMedium>
              {tag && <StyledTag>Current</StyledTag>}
            </Flex>
          </StyledMenuItem>
        </ItemContainer>
      ))}

      {otherMenuItems?.length > 0 ? (
        <StyledMenuGroup title="Other Time Periods">
          {otherMenuItems.map(({ label, value }) => (
            <ItemContainer withBackground key={value}>
              <StyledOtherMenuItem
                value={value}
                onClick={() => handleSelect(value)}
                $isSelected={value === selectedValue}
              >
                <TextBodyMedium>{label}</TextBodyMedium>
              </StyledOtherMenuItem>
            </ItemContainer>
          ))}
        </StyledMenuGroup>
      ) : null}
    </StyledMenuList>
  </Menu>
);

const TimeBoundSelect = ({
  onChange: handleSelect,
  menuItems,
  otherMenuItems = [],
  selectedValue,
  defaultValue,
  isUnderlineStyle = false,
  isStyle2 = false,
  renderAsRefableComp = forwardRef((props, ref) => <Button ref={ref} variant="tertiary" p={0} {...props} />),
  isPageHeaderTimeline = false,
  showCalendarIcon = false,
  ...props
}) => {
  const selectedTimeBoundObject = useMemo(
    () => [...menuItems, ...otherMenuItems].find((pr) => pr.value === selectedValue),
    [menuItems, otherMenuItems, selectedValue]
  );

  const CurrentTimelineWrapper = isPageHeaderTimeline ? HeaderText : React.Fragment;

  if (isPageHeaderTimeline) {
    return (
      <Flex
        sx={{
          cursor: 'pointer',
          borderBottom: '1px dashed #999999',
          transition: 'border-color 0.3s ease',
          '&:hover': {
            borderBottom: '1px solid #333333 ',
          },
        }}
      >
        <CommonTimelineSelect
          CurrentTimelineWrapper={CurrentTimelineWrapper}
          menuItems={menuItems}
          onChange={handleSelect}
          selectedTimeBoundObject={selectedTimeBoundObject}
          selectedValue={selectedValue}
          otherMenuItems={otherMenuItems}
          renderAsRefableComp={renderAsRefableComp}
          menuButtonSx={{
            margin: '2px -10px 0px -13px',
            '&[aria-expanded="true"]': {
              background: 'transparent',
            },
            svg: {
              color: '#333333 !important',
            },
            height: '32px !important',
          }}
        />
      </Flex>
    );
  }

  return (
    <Box display="flex" alignItems="center" marginTop="auto">
      <CommonTimelineSelect
        CurrentTimelineWrapper={CurrentTimelineWrapper}
        menuItems={menuItems}
        onChange={handleSelect}
        selectedTimeBoundObject={selectedTimeBoundObject}
        selectedValue={selectedValue}
        otherMenuItems={otherMenuItems}
        renderAsRefableComp={renderAsRefableComp}
        showCalendarIcon={showCalendarIcon}
      />
    </Box>
  );
};

const ItemContainer = styled.div`
  padding: 2px 8px;
  background: ${(props) => (props.withBackground ? '#fafafa' : 'inherit')};
`;

const StyledMenuGroup = styled(MenuGroup)`
  position: sticky;
  background: #fafafa;
  width: 100%;

  top: 0px;

  margin: 0px;
  margin-top: 2px;

  padding: 8px;
  padding-bottom: 4px;
`;

const StyledMenuItem = styled(MenuItem)`
  padding: 8px;

  border-radius: 4px;

  &:hover {
    background: #e6f1fd;
  }
  background: ${({ $isSelected }) => $isSelected && '#e6f1fd'};
  p {
    color: ${({ $isSelected }) => ($isSelected ? '#104F95' : '#333333')};
  }
`;

const StyledOtherMenuItem = styled(StyledMenuItem)`
  background: ${({ $isSelected }) => ($isSelected ? '#e6f1fd' : '#fafafa')};
`;

const StyledTag = styled(Tag)``;

const StyledMenuList = styled(MenuList)`
  & > div:first-child {
    padding-top: 8px;
  }

  & > div > div:last-child {
    padding-bottom: 8px;
  }
`;

export default TimeBoundSelect;
